import moment from 'moment';
import type { ExpiryTimeGranularity } from '@x-guard/xgac-types/xgac';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useCallback, useEffect, useMemo } from 'react';
import { required, useTranslate } from 'react-admin';
import {
  Box, Chip, Stack, Tooltip, Typography,
} from '@mui/material';
import { GrayDateInputWithUpperLabel } from './inputs/GrayDateInputWithUpperLabel';
import { getExpiryTimeGranularity, TimeGranularityLabel } from '../../../utils/timeGranularityUtils';

const calculateExpiryDate = (startDate: string | undefined, timeGranularity: ExpiryTimeGranularity | undefined) => {

  if (!startDate || !timeGranularity || !timeGranularity?.type || !timeGranularity?.value === null) {

    return undefined;

  }

  return moment(startDate).add(timeGranularity.value, timeGranularity.type).format('YYYY-MM-DD');

};

const useExpiryDate = (startDateSource: string, expiryDateSource: string, timeGranularity: ExpiryTimeGranularity | undefined) => {

  const { setValue } = useFormContext();
  const { dirtyFields } = useFormState();
  const startDate = useWatch({ name: startDateSource });
  const expiryDate = useWatch({ name: expiryDateSource });

  const calculatedExpiryDate = useMemo(() => {

    return calculateExpiryDate(startDate, timeGranularity);

  }, [startDate, timeGranularity]);

  useEffect(() => {

    if (!dirtyFields[startDateSource] || !calculatedExpiryDate) {

      return;

    }

    setValue(expiryDateSource, calculatedExpiryDate, { shouldDirty: true, shouldValidate: true });

  }, [calculatedExpiryDate, expiryDateSource, setValue, dirtyFields, startDateSource]);

  const applyExpiryDate = useCallback(() => {

    if (!calculatedExpiryDate) {

      return;

    }

    setValue(expiryDateSource, calculatedExpiryDate, { shouldDirty: true, shouldValidate: true });

  }, [calculatedExpiryDate, expiryDateSource, setValue]);

  const isOverridden = useMemo(
    () => {

      return calculatedExpiryDate != null
      && expiryDate != null
      && moment(expiryDate).diff(moment(calculatedExpiryDate), 'days') !== 0;

    },
    [expiryDate, calculatedExpiryDate],
  );

  return {
    calculatedExpiryDate,
    applyExpiryDate,
    isOverridden,
    startDate,
    expiryDate,
  };

};

export const ExpiryDateDisplay = (props: {
  startDateSource: string;
  expiryDateSource: string;
  timeGranularity?: ExpiryTimeGranularity;

  startDateLabel: string;
  expiryDateLabel: string;
  helperText: string;
}) => {

  const translate = useTranslate();

  const {
    calculatedExpiryDate, applyExpiryDate, isOverridden, startDate, expiryDate,
  } = useExpiryDate(props.startDateSource, props.expiryDateSource, props.timeGranularity);

  const timeGranularityRounded = useMemo(() => {

    if (!startDate || !expiryDate) {

      return props.timeGranularity ?? undefined;

    }

    return getExpiryTimeGranularity(startDate, expiryDate);

  }, [startDate, expiryDate, props.timeGranularity]);

  const minExpiryDate = useMemo(() => {

    if (!startDate) return undefined;
    return moment(startDate).startOf('day').format('YYYY-MM-DD');

  }, [startDate]);

  return (
    <Stack spacing={1}>
      <Box sx={{
        display: 'flex', flexDirection: 'column', gap: 0, alignItems: 'flex-start',
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          maxHeight: 70,
          flexDirection: 'row',
          flex: 1,
          maxWidth: 500,
        }}>
          <Box sx={{ maxWidth: 160 }}>
            <GrayDateInputWithUpperLabel
              source={props.startDateSource}
              label={props.startDateLabel}
              size="small"
              sx={{
                width: 160,
              }}
              validate={required()}
            />
          </Box>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography component="span">+</Typography>
            <Tooltip title={calculatedExpiryDate !== expiryDate ? translate('resources.expiry_date_display.text.click_to_reset_expiry') : undefined}>
              <Chip
                label={TimeGranularityLabel(timeGranularityRounded, translate)}
                onClick={calculatedExpiryDate ? applyExpiryDate : undefined}
                clickable={calculatedExpiryDate !== expiryDate}
                color={isOverridden ? 'warning' : 'primary'}
              />
            </Tooltip>
            <Typography component="span">=</Typography>
          </Box>
          <Box sx={{ maxWidth: 160 }}>
            <GrayDateInputWithUpperLabel
              source={props.expiryDateSource}
              label={props.expiryDateLabel}
              size="small"
              inputProps={{
                min: minExpiryDate,
              }}
              sx={{
                width: 160,
              }}
              validate={required()}
            />
          </Box>
        </Box>
        <Box sx={{ maxWidth: 450, marginTop: 1 }}>
          <Typography color="text.secondary" sx={{
            fontSize: 10, maxLines: 2, overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {props.helperText}
          </Typography>
        </Box>
      </Box>
    </Stack>
  );

};
